import { useState, useEffect } from "react";
import { AppSidebar } from "@/components/app-sidebar";
import { ChartAreaInteractive } from "@/components/chart-area-interactive";
import { DataTable } from "@/components/data-table";
import { PolicyManagement } from "@/components/policy-management";
import { RateLimitStatus } from "@/components/rate-limit-status";
import { SectionCards } from "@/components/section-cards";
import { SiteHeader } from "@/components/site-header";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  useDNSQueryStats,
  useDNSActivityData,
  useRecentBlocks,
} from "@/hooks/useDNSAnalytics";
import { useOrganizationPolicies } from "@/hooks/useDNSPolicies";
import { useUserOrganization } from "@/hooks/useOrganization";
import { Copy, CheckIcon, Globe } from "lucide-react";

export default function Page() {
  const { data: organization } = useUserOrganization();
  const { data: stats, isLoading: statsLoading } = useDNSQueryStats();
  const { data: activityData, isLoading: activityLoading } =
    useDNSActivityData();
  const { data: policies = [], isLoading: policiesLoading } =
    useOrganizationPolicies();
  const { data: recentBlocks = [], isLoading: blocksLoading } =
    useRecentBlocks(50);
  const [copied, setCopied] = useState(false);

  const dohUrl = organization?.dns_token
    ? `https://dns.fendly.org/dns-query/${organization.dns_token}`
    : null;

  const handleCopyDoh = () => {
    if (!dohUrl) return;
    navigator.clipboard.writeText(dohUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isLoading =
    statsLoading || activityLoading || policiesLoading || blocksLoading;

  // Log data for debugging
  useEffect(() => {
    console.log("📊 Dashboard data:", {
      organization,
      stats,
      activityData,
      policies,
      recentBlocks,
      isLoading,
    });
  }, [organization, stats, activityData, policies, recentBlocks, isLoading]);

  // Show loading state if data is being fetched
  if (isLoading) {
    return (
      <SidebarProvider
        style={
          {
            "--sidebar-width": "16rem",
            "--header-height": "3rem",
          } as React.CSSProperties
        }
      >
        <AppSidebar variant="inset" />
        <SidebarInset>
          <SiteHeader />
          <div className="flex flex-1 flex-col">
            <div className="@container/main flex flex-1 flex-col gap-6 p-6">
              <div className="flex items-center justify-center h-full">
                <div className="text-foreground text-lg">
                  Loading dashboard...
                </div>
              </div>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  // Show setup message if no organization
  if (!organization) {
    return (
      <SidebarProvider
        style={
          {
            "--sidebar-width": "16rem",
            "--header-height": "3rem",
          } as React.CSSProperties
        }
      >
        <AppSidebar variant="inset" />
        <SidebarInset>
          <SiteHeader />
          <div className="flex flex-1 flex-col">
            <div className="@container/main flex flex-1 flex-col gap-6 p-6">
              <div className="flex items-center justify-center h-full">
                <div className="text-foreground text-lg">
                  Please set up your organization first
                </div>
              </div>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": "16rem",
          "--header-height": "3rem",
        } as React.CSSProperties
      }
    >
      <AppSidebar variant="inset" />
      <SidebarInset>
        <SiteHeader />
        <div className="flex flex-1 flex-col">
          <div className="@container/main flex flex-1 flex-col gap-6 p-6">
            <SectionCards />

            {dohUrl && (
              <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-card">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Globe className="size-4 text-primary" />
                    Your DNS-over-HTTPS URL
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    Configure your devices or router to use this URL for DNS
                    filtering. It is unique to your organization.
                  </p>
                  <div className="flex items-center gap-2 rounded-lg border border-primary/20 bg-background px-3 py-2">
                    <code className="flex-1 truncate text-sm font-mono text-foreground">
                      {dohUrl}
                    </code>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleCopyDoh}
                      className="shrink-0 h-7 px-2"
                    >
                      {copied ? (
                        <CheckIcon className="size-3.5 text-green-500" />
                      ) : (
                        <Copy className="size-3.5" />
                      )}
                    </Button>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Full setup instructions are available in{" "}
                    <a
                      href="/dashboard/settings"
                      className="text-primary underline underline-offset-2"
                    >
                      Settings → DNS
                    </a>
                    .
                  </p>
                </CardContent>
              </Card>
            )}

            <ChartAreaInteractive />

            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-3 bg-background/80 backdrop-blur-xl border border-primary/20">
                <TabsTrigger
                  value="overview"
                  className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-foreground"
                >
                  Overview
                </TabsTrigger>
                <TabsTrigger
                  value="policies"
                  className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-foreground"
                >
                  DNS Policies
                </TabsTrigger>
                <TabsTrigger
                  value="rate-limits"
                  className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-foreground"
                >
                  Rate Limits
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-6">
                <div className="grid grid-cols-1 gap-6">
                  <div className="w-full">
                    <DataTable data={recentBlocks} />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="policies" className="mt-6">
                <PolicyManagement />
              </TabsContent>

              <TabsContent value="rate-limits" className="mt-6">
                <RateLimitStatus 
                  dnsServerUrl={import.meta.env.VITE_DNS_SERVER_URL ?? 'http://localhost:3001'}
                  refreshInterval={30000}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
